import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, Coins, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Trial Dashboard Widget — shows days/credits remaining + upgrade CTA.
 * Hidden when the user has converted to a paid subscription or is an admin.
 */
export default function TrialWidget() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/trial/status");
      setData(r.data);
    } catch { /* hide on error */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  // Hide for admins or already-converted paying users
  if (data.admin_unlimited || data.converted) return null;
  // Hide if user isn't on trial at all (legacy free-plan users)
  if (!data.on_trial && !data.in_grace && !data.hard_expired) return null;

  const onTrial = data.on_trial;
  const inGrace = data.in_grace;
  const expired = data.hard_expired;
  const ribbon = expired ? "TRIAL EXPIRED" : inGrace ? "GRACE PERIOD" : "FREE TRIAL";
  const ribbonCls = expired ? "bg-rose-500/20 text-rose-300"
                   : inGrace ? "bg-brand-amber/20 text-brand-amber"
                             : "bg-emerald-500/20 text-emerald-300";

  return (
    <div
      className={`rounded-xl border p-6 ${expired ? "border-rose-500/40 bg-rose-500/5"
                                                 : inGrace ? "border-brand-amber/40 bg-brand-amber/5"
                                                           : "border-line bg-gradient-to-br from-brand-cyan/5 to-brand-amber/5"}`}
      data-testid="trial-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand-amber" />
          <h3 className="font-display text-lg font-bold">BroadcastFlow Free Trial</h3>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${ribbonCls}`}
              data-testid="trial-ribbon">
          {ribbon}
        </span>
      </div>

      {onTrial && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Tile icon={Clock} label="Days remaining" value={data.days_remaining} accent="emerald" testid="trial-days" />
          <Tile icon={Coins} label="Credits left" value={`${data.credits_remaining ?? "—"} / ${data.credits_initial ?? data.trial_initial_credits}`} accent="amber" testid="trial-credits" />
        </div>
      )}

      {inGrace && (
        <div className="mb-4 text-sm">
          <div className="flex items-center gap-2 text-brand-amber font-semibold mb-1">
            <AlertTriangle className="h-4 w-4" /> Your trial has ended.
          </div>
          <p className="text-ink-secondary">
            You have <strong className="text-ink-primary">{data.days_remaining} days</strong> left to view your content before access is locked. Upgrade now to keep generating.
          </p>
        </div>
      )}

      {expired && (
        <div className="mb-4 text-sm">
          <div className="flex items-center gap-2 text-rose-300 font-semibold mb-1">
            <AlertTriangle className="h-4 w-4" /> Your free trial has expired.
          </div>
          <p className="text-ink-secondary">
            All your content is preserved. Upgrade now to restore full access — your stations, integrations, and AI history pick up right where you left off.
          </p>
        </div>
      )}

      <Link
        to="/dashboard/billing?tab=plan"
        data-testid="trial-upgrade-cta"
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black hover:bg-brand-amberHover transition"
      >
        Upgrade now <ArrowRight className="h-4 w-4" />
      </Link>

      {onTrial && (
        <div className="mt-3 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          Full platform access · all AI tools unlocked
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, testid }) {
  const toneCls = { amber: "text-brand-amber", emerald: "text-emerald-400", rose: "text-rose-400" }[accent] || "";
  return (
    <div className="rounded-lg border border-line bg-bg-elevated/60 p-3" data-testid={testid}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className={`h-3 w-3 ${toneCls}`} />
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</span>
      </div>
      <div className={`font-display text-2xl font-bold ${toneCls}`}>{value}</div>
    </div>
  );
}
